/**
 * @file ResetConfirmPage.tsx
 * @description Страница /reset/:token (phase08.5 shadcn; phase19.29 — тема
 * и логотип Commora через CommoraAuthShell).
 *
 * phase19.28 этап А (PL7): по ссылке приглашения владельца (`?invite=1`) —
 * та же форма с текстами «Добро пожаловать в Commora» / «Задайте пароль»
 * вместо восстановления. Тексты — реестр `TEXTS` по режиму.
 */

import { Loader2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link as ReactRouterLink, useNavigate, useParams, useSearchParams } from "react-router";

import { CommoraAuthShell } from "../../components/brand/CommoraAuthShell";
import { EditorialCard } from "../../components/ui/EditorialCard";
import { Alert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ApiError } from "../../lib/api-client";
import { confirmPasswordReset } from "../../lib/api-auth-reset";

type Mode = "reset" | "invite";

const TEXTS: Readonly<Record<Mode, Record<"tab" | "title" | "lead" | "password" | "submit" | "done" | "badLink" | "failed", string>>> = {
  reset: {
    tab: "Новый пароль",
    title: "Новый пароль",
    lead: "Задайте новый пароль для учётной записи.",
    password: "Новый пароль (≥10 символов)",
    submit: "Сменить пароль",
    done: "Пароль обновлён. Войдите с новым паролем.",
    badLink: "Ссылка восстановления некорректна",
    failed: "Не удалось сменить пароль",
  },
  invite: {
    tab: "Задайте пароль",
    title: "Добро пожаловать в Commora",
    lead: "Задайте пароль — с ним вы будете входить в кабинет магазина.",
    password: "Пароль (≥10 символов)",
    submit: "Задать пароль",
    done: "Пароль задан. Войдите в кабинет магазина.",
    badLink: "Ссылка приглашения некорректна",
    failed: "Не удалось задать пароль",
  },
};

export function ResetConfirmPage() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const t = TEXTS[params.get("invite") === "1" ? "invite" : "reset"];
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!token) {
      setError(t.badLink);
      return;
    }
    if (password.length < 10) {
      setError("Пароль должен быть не короче 10 символов");
      return;
    }
    if (password !== repeat) {
      setError("Пароли не совпадают");
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.failed);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <CommoraAuthShell tabTitle={t.tab}>
      <EditorialCard floating className="w-full">
        <EditorialCard.Body>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1 items-center text-center">
              <h1 className="type-page-title text-on-surface">{t.title}</h1>
              {!done ? (
                <p className="text-sm text-on-surface-variant">{t.lead}</p>
              ) : null}
            </div>

            {done ? (
              <>
                <Alert severity="success" role="status">
                  {t.done}
                </Alert>
                <Button
                  type="button"
                  onClick={() => navigate("/login", { replace: true })}
                  className="w-full"
                >
                  Войти
                </Button>
              </>
            ) : (
              <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="reset-password">{t.password}</Label>
                  <Input
                    id="reset-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={10}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="reset-password-repeat">Повторите пароль</Label>
                  <Input
                    id="reset-password-repeat"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={10}
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value)}
                    disabled={submitting}
                  />
                </div>

                {error ? <Alert severity="error">{error}</Alert> : null}

                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Сохраняем…
                    </>
                  ) : (
                    t.submit
                  )}
                </Button>
              </form>
            )}

            <div className="text-center">
              <ReactRouterLink to="/login" className="text-sm text-primary hover:underline">
                ← Вернуться к входу
              </ReactRouterLink>
            </div>
          </div>
        </EditorialCard.Body>
      </EditorialCard>
    </CommoraAuthShell>
  );
}
